<template>
  <div class="animated fadeIn">
    <b-card header="Search Filter">
      <b-form>
        <b-row>
          <b-col md="6" class="mb-3">
            <label for="keyword">Keyword</label>
            <b-input-group>
              <b-input-group-prepend><b-input-group-text><i class="icon-magnifier"/></b-input-group-text></b-input-group-prepend>
              <b-form-input id="keyword" v-model.trim="form.keyword" type="text" class="form-control" name="keyword" />
            </b-input-group>
          </b-col>
          <b-col md="6" class="mb-3">
            <label for="status">Status</label>
            <b-form-select id="status" v-model="form.status" :options="statusOptions" name="status" />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6" class="mb-3">
            <label for="start-date">Start Date</label>
            <b-input-group>
              <b-input-group-prepend><b-input-group-text><i class="icon-calendar"/></b-input-group-text></b-input-group-prepend>
              <date-pick v-model="form.start_date" :display-format="'DD-MM-YYYY'" :input-attributes="{class:'form-control', id:'start-date', name:'start_date'}"/>
            </b-input-group>
          </b-col>
          <b-col md="6" class="mb-3">
            <label for="end-date">End Date</label>
            <b-input-group>
              <b-input-group-prepend><b-input-group-text><i class="icon-calendar"/></b-input-group-text></b-input-group-prepend>
              <date-pick v-model="form.end_date" :display-format="'DD-MM-YYYY'" :input-attributes="{class:'form-control', id:'end-date', name:'end_date'}"/>
            </b-input-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="4" class="mb-3">
            <label for="province">Province</label>
            <b-form-select id="province" v-model="form.province_id" :options="provinceOptions" name="province" @change="handleGetCities" />
          </b-col>
          <b-col md="4" class="mb-3">
            <label for="city">City</label>
            <b-form-select id="city" v-model="form.city_id" :options="cityOptions" :disabled="noProvince" name="city" @change="handleGetDistricts" />
          </b-col>
          <b-col md="4" class="mb-3">
            <label for="district">District</label>
            <b-form-select id="district" v-model="form.district_id" :options="districtOptions" :disabled="noCity" name="district" />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6" class="mb-3">
            <label for="sort-by">Sort By</label>
            <b-form-select id="sort-by" v-model="form.sort_by" :options="sortOptions" name="sort_by" />
          </b-col>
          <b-col md="6" class="mb-3">
            <label for="per_page">Item per Page</label>
            <b-form-select id="per-page" v-model="form.per_page" :options="perPageOptions" name="per_page" />
          </b-col>
        </b-row>
        <b-button :disabled="storeState.isChildLoading" variant="primary" @click="handleSearchShippings">Search</b-button>
        <b-button :disabled="storeState.isChildLoading" variant="success" class="ml-2" @click="handleDownloadShippings">Download</b-button>
      </b-form>
    </b-card>
    <b-card header="Shippings List">
      <b-table v-if="items" :items="items" :fields="fields" bordered hover responsive class="text-nowrap">
        <template slot="shipping_cost" slot-scope="data">
          {{ data.item.shipping_cost | toRupiah }}
        </template>
        <template slot="total_gold_weight" slot-scope="data">
          {{ data.item.total_gold_weight }} gr
        </template>
        <template slot="total_certificate_cost" slot-scope="data">
          {{ data.item.total_certificate_cost | toRupiah }}
        </template>
        <template slot="total_payment" slot-scope="data">
          {{ data.item.total_payment | toRupiah }}
        </template>
        <template slot="partner_shipping_incentive" slot-scope="data">
          {{ data.item.partner_shipping_incentive | toRupiah }}
        </template>
        <template slot="shipped_at" slot-scope="data">
          <span v-if="data.item.shipped_at != '0001-01-01 00:00:00'">{{ data.item.shipped_at }}</span>
        </template>
        <template slot="delivered_at" slot-scope="data">
          <span v-if="data.item.delivered_at != '0001-01-01 00:00:00'">{{ data.item.delivered_at }}</span>
        </template>
      </b-table>
      <p v-if="!items" class="mb-0">No data</p>
      <b-pagination v-if="items && totalRows > params.per_page" :disabled="storeState.isChildLoading" :total-rows="totalRows" v-model="params.page" :per-page="params.per_page" align="center" first-text="First" prev-text="Prev" next-text="Next" last-text="Last" size="md" class="mt-4" @change="updateQueryString" />
    </b-card>
  </div>
</template>

<script>
import DatePick from 'vue-date-pick'
import { store } from '@/shared/store'
import { toRupiah, handleDownloadFile } from '@/shared/utils'
import { getShippingsList, getShippingsListCsv, getProvinces, getCities, getDistricts, handleError } from '@/services/metal'

export default {
  name: 'ShippingsList',
  components: { DatePick },

  filters: {
    toRupiah: function(val) {
      return toRupiah(val)
    }
  }, // filters

  data() {
    return {
      params: {},
      form: {},
      defaultSelectOption: { value: '', text: 'All'},
      provinceOptions: [],
      cityOptions: [],
      districtOptions: [],
      statusOptions: [
        { value: '', text: 'All' },
        { value: 'PAID', text: 'Paid' },
        { value: 'READY_PICKUP', text: 'Ready Pickup' },
        { value: 'SHIPPED', text: 'Shipped' },
        { value: 'DELIVERED', text: 'Delivered' },
        { value: 'CANCEL', text: 'Cancel' },
      ],
      sortOptions: [
        { value: 'date_desc', text: 'Newest date' },
        { value: 'date_asc', text: 'Oldest date' },
        { value: 'gram_desc', text: 'Highest gram' },
        { value: 'gram_asc', text: 'Lowest gram' },
      ],
      perPageOptions: [
        { value: 10, text: '10' },
        { value: 25, text: '25' },
        { value: 50, text: '50' },
        { value: 75, text: '75' },
        { value: 100, text: '100' }
      ],
      fields: [
        {
          key: 'created_at',
          label: 'Date'
        },
        'customer_name',
        'customer_phone',
        'customer_email',
        {
          key: 'external_customer_id',
          label: 'Customer ID'
        },
        'status',
        'shipping_method',
        'contract_number',
        'trx_ref_number',
        'receiver_name',
        'receiver_phone',
        'receiver_address',
        'receiver_province',
        'receiver_city',
        'receiver_district',
        'receiver_postal_code',
        'shipping_cost',
        {
          key: 'resi_number',
          label: 'Airway Bill',
        },
        {
          key: 'gold_config',
          label: 'Gold Info',
          formatter: 'parseGoldConfig',
          tdAttr: {style: 'white-space: pre'}
        },
        'total_gold_weight',
        'total_gold_piece',
        'total_certificate_cost',
        'total_payment',
        {
          key: 'partner_shipping_incentive',
          label: 'Incentive',
        },
        'shipped_at',
        'delivered_at',
      ],
      items: null,
      totalRows: 0,
      storeState: store.state,
    }
  }, // data

  computed: {
    noProvince: function() {
      return this.form.province_id == ''
    },
    noCity: function() {
      return this.form.city_id == ''
    },
  }, // computed

  watch: {
    '$route': function() {
      store.setIsChildLoading(true)

      this.initParams()

      if (this.form.province_id != '') {
        this.handleGetCities(this.form.province_id, false)
      }

      if (this.form.city_id != '') {
        this.handleGetDistricts(this.form.city_id, false)
      }

      this.handleGetShippingsList()
    },
  }, // watch

  created() {
    store.setIsInitLoading(true)

    this.provinceOptions = [ this.defaultSelectOption ]
    this.cityOptions = [ this.defaultSelectOption ]
    this.districtOptions = [ this.defaultSelectOption ]

    this.initParams()

    getProvinces()
      .then(response => {
        response.data.data.forEach( data => {
          let provinceOption = {
            value: data.province_id,
            text: data.province_name
          }
          this.provinceOptions.push(provinceOption)
        })
      })
      .catch( error => {
        handleError(error)
      })
      .then( () => {
        store.clearProcessing()
      })

    if (this.form.province_id != '') {
      this.handleGetCities(this.form.province_id, false)
    }

    if (this.form.city_id != '') {
      this.handleGetDistricts(this.form.city_id, false)
    }

    this.handleGetShippingsList()
  }, // created

  methods: {
    initParams() {
      // assign default value
      this.params = {
        page: 1,
        keyword: '',
        status: '',
        start_date: '',
        end_date: '',
        province_id: '',
        city_id: '',
        district_id: '',
        sort_by: 'date_desc',
        per_page: 10,
      }

      // overwrite value from query string
      if (Object.keys(this.$route.query).length > 0) {
        if (this.$route.query.page) {
          this.$route.query.page = parseInt(this.$route.query.page)
        }

        if (this.$route.query.per_page) {
          this.$route.query.per_page = parseInt(this.$route.query.per_page)
        }

        this.params = Object.assign(this.params, this.$route.query)
      }

      // overwrite form value
      this.form = Object.assign({}, this.params)
    },

    handleGetShippingsList() {
      store.setIsProcessing(true)

      let reqData = {
        search: this.params.keyword,
        status: this.params.status,
        start_date: this.params.start_date,
        end_date: this.params.end_date,
        province_id: this.params.province_id,
        city_id: this.params.city_id,
        district_id: this.params.district_id,
        sort_by: this.params.sort_by,
        offset: (this.params.page-1)*this.params.per_page,
        limit: this.params.per_page,
      }

      getShippingsList(reqData)
        .then( response => {
          this.totalRows = response.data.data.total_data

          if (this.totalRows != 0) {
            this.items = response.data.data.shipping_list
          } else {
            this.items = null
          }

          store.setIsInitLoading(false)
        })
        .catch( error => {
          handleError(error)
        })
        .then( () => {
          store.clearProcessing()
        })
    },

    updateQueryString(page) {
      this.params.page = page
      this.$router.push({ path: '/shippings-list', query: this.params})
    },

    handleSearchShippings() {
      // overwrite params value using form value
      this.params = Object.assign(this.params, this.form)
      this.updateQueryString(1)
    },

    handleDownloadShippings() {
      // overwrite params value using form value
      this.params = Object.assign(this.params, this.form)

      let reqData = {
        search: this.params.keyword,
        status: this.params.status,
        start_date: this.params.start_date,
        end_date: this.params.end_date,
        province_id: this.params.province_id,
        city_id: this.params.city_id,
        district_id: this.params.district_id,
        sort_by: this.params.sort_by,
      }

      getShippingsListCsv(reqData)
        .then( response => {
          handleDownloadFile(response)
          store.setIsInitLoading(false)
        })
        .catch( error => {
          handleError(error)
        })
        .then( () => {
          store.clearProcessing()
        })
    },

    handleGetCities(province_id, isLoading = true) {
      if (province_id == '') {
        this.form.city_id = ''
        this.form.district_id = ''
        return false
      }

      if (isLoading) {
        store.setIsChildLoading(true)
        store.setIsProcessing(true)
      }

      let data = { province_id: province_id }

      getCities(data)
        .then(response => {
          this.cityOptions = [ this.defaultSelectOption ]

          let cityFound = false

          response.data.data.forEach( data => {
            let cityOption = {
              value: data.city_id,
              text: data.city_name
            }
            this.cityOptions.push(cityOption)

            if (this.form.city_id == data.city_id) {
              cityFound = true
            }
          })

          // reset city and district select options
          if (!cityFound) {
            this.form.city_id = ''
            this.form.district_id = ''
          }
        })
        .catch( error => {
          this.cityOptions = [ this.defaultSelectOption ]
          this.districtOptions = [ this.defaultSelectOption ]

          this.form.city_id = ''
          this.form.district_id = ''

          handleError(error)
        })
        .then( () => {
          store.clearProcessing()
        })
    },

    handleGetDistricts(city_id, isLoading = true) {
      if (city_id == '') {
        this.form.district_id = ''
        return false
      }

      if (isLoading) {
        store.setIsChildLoading(true)
        store.setIsProcessing(true)
      }

      let data = { city_id: city_id }

      getDistricts(data)
        .then(response => {
          this.districtOptions = [ this.defaultSelectOption ]

          let districtFound = false

          response.data.data.forEach( data => {
            let districtOption = {
              value: data.district_id,
              text: data.district_name
            }
            this.districtOptions.push(districtOption)

            if (this.form.district_id == data.district_id) {
              districtFound = true
            }
          })

          // reset district select option
          if (!districtFound) {
            this.form.district_id = ''
          }
        })
        .catch( error => {
          this.districtOptions = [ this.defaultSelectOption ]
          this.form.district_id = ''

          handleError(error)
        })
        .then( () => {
          store.clearProcessing()
        })
    },

    parseGoldConfig(value) {
      let goldConfig = ''
      if (value != '') {
        let obj = JSON.parse(value)
        obj.forEach(function(data){
          goldConfig += data.gram + ' gr x' + data.amount + '\n'
        })
      }
      return goldConfig
    },
  }, // methods
}
</script>
